// src/firestore/path.ts

type FirestorePathSegment = string
type FirestorePath = string

const MAX_FIRESTORE_ID_LENGTH = 1500

/**
 * Assembles a Firestore Path string by joining `segments` with a `/` character.
 */
export function assemble(...segments: FirestorePathSegment[]): FirestorePath {
  return _assemble(null, segments)
}

/**
 * Appends to a Firestore Path `basePath` string by joining additional `segments` with a `/` character.
 */
export function append(
  basePath: FirestorePath,
  ...segments: FirestorePathSegment[]
): FirestorePath {
  return _assemble(basePath, segments)
}

class InvalidPathError extends Error {
  constructor(reason: string, assembledPath: FirestorePath) {
    super(`${reason}: ${assembledPath}`)
  }
}

class InvalidPathSegmentError extends InvalidPathError {
  constructor(arg: FirestorePathSegment, assembledPath: FirestorePath) {
    super(`Invalid path segment received: "${arg}"`, assembledPath)
  }
}

class InvalidPathLengthError extends InvalidPathError {
  constructor(assembledPath: FirestorePath) {
    super('Path is too long', assembledPath)
  }
}

function _assemble(
  prefix: string | null,
  segments: FirestorePathSegment[]
): FirestorePath {
  const assembled =
    prefix === null ? segments.join('/') : [prefix, ...segments].join('/')
  segments.forEach((segment) => {
    if (!segment || segment.indexOf('/') !== -1) {
      throw new InvalidPathSegmentError(segment, assembled)
    }
    if (segment === '.' || segment === '..' || /__.*__/.test(segment)) {
      throw new InvalidPathSegmentError(segment, assembled)
    }
  })
  if (assembled.length > MAX_FIRESTORE_ID_LENGTH) {
    throw new InvalidPathLengthError(assembled)
  }
  return assembled
}
